import React from 'react';
import parse from 'html-react-parser';

import sanitizeHtml from 'sanitize-html';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ArticleNumberedHeading from '../ArticleNumberedHeading';
import ArticleImageBlock from '../ArticleImageBlock';
import ArticleButton from '../ArticleButton';
import ArticleButtonGroup from '../ArticleButtonGroup';
import ArticleExpandableReview from '../ArticleExpandableReview';
import ArticleList from '../ArticleList';
import ArticleRatedFeaturesList from '../ArticleRatedFeaturesList';
import * as style from './ArticleProductReviewBlock.module.scss';
import ArticleStarRating from '../ArticleStarRating/ArticleStarRating';

/**
 * Product review block.
 */
const ArticleProductReviewBlock = (props) => {
  const {
    anchor,
    number,
    title,
    titleTag,
    imgData,
    altText,
    productFeatures,
    productReviewText,
    pros,
    cons,
    button,
  } = props;

  const { t } = useTranslation();

  const formatReviewText = (plainHtml) => {
    return parse(sanitizeHtml(plainHtml));
  };

  return (
    <div className={style.productReviewBlock}>
      <ArticleNumberedHeading
        number={number}
        title={title}
        titleTag={titleTag}
        variant="short"
        id={anchor}
      />
      <div className={style.topBlock}>
        <ArticleImageBlock imgData={imgData} altText={altText} />
        <div className={style.imageButtonColumn}>
          {productFeatures && (
            <>
              <ArticleStarRating
                features={productFeatures}
                className={style.articleStarRating}
              />
              <ArticleRatedFeaturesList features={productFeatures} />
            </>
          )}
          <ArticleButtonGroup className={style.buttonGroup}>
            <ArticleButton
              text={button?.shopButton}
              variant="primary"
              href={button?.shopLink}
              hrefTarget={button?.shopLinkOpenInNewTab ? '_blank' : '_self'}
            />
          </ArticleButtonGroup>
        </div>
      </div>
      <ArticleExpandableReview
        collapsedText={t('articles.productReviewBlock.readOurReview')}
        expandedText={t('articles.productReviewBlock.minimize')}
      >
        {formatReviewText(productReviewText)}
        {(pros?.prosTitle && pros?.prosList) ||
        (cons?.consTitle && cons?.consList) ? (
          <div className={style.prosAndCons}>
            <h5>{pros?.prosTitle}</h5>
            <ArticleList listItems={pros?.prosList} />
            <h5>{cons?.consTitle}</h5>
            <ArticleList listItems={cons?.consList} />
          </div>
        ) : null}
      </ArticleExpandableReview>
    </div>
  );
};

ArticleProductReviewBlock.defaultProps = {
  altText: '',
  anchor: '',
  titleTag: 'h3',
};

ArticleProductReviewBlock.propTypes = {
  /** Number to display in the circle */
  number: PropTypes.number.isRequired,
  /** The title */
  title: PropTypes.string.isRequired,
  /** The Tag used to display the title (<h2>, <h3>, <h4>, <p>) */
  titleTag: PropTypes.string,
  /** The image data (gatsby-image) */
  imgData: PropTypes.object.isRequired,
  /** Image alt text for when image is not displayed (and SEO) */
  altText: PropTypes.string,
  /** Product review text (plain HTML) */
  productReviewText: PropTypes.string.isRequired,
  /** Product features - a list with 0-5 scores */
  productFeatures: PropTypes.array.isRequired,
  /** A list of pros + pros section title */
  pros: PropTypes.object.isRequired,
  /** A list of cons + cons section title */
  cons: PropTypes.object.isRequired,
  /** Button text and link */
  button: PropTypes.object.isRequired,
  /** Anchor passed to the heading block for anchor navigation */
  anchor: PropTypes.string,
};

export default ArticleProductReviewBlock;
